import { wencesApi } from "@/api/wencesApi";

/**
 * Símbolos que no se confunden con otra moneda. El resto de las monedas se muestra con su
 * sigla ("CNY 1.000"): el "$" de ARS y el de USD son el mismo carácter, y en un panel de
 * cobros eso no es un detalle estético.
 */
const UNMISTAKABLE_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
};

/**
 * Moneda en la que está expresada la operación, para los importes del panel financiero.
 *
 * Hasta el ticket #397 el panel escribía "$" delante de cada importe, así que una operación
 * en euros se leía como si fuera en dólares. Cada importe se muestra en su moneda; con
 * exchange_rates_module activo el balance además lo pasa a la moneda del sistema.
 *
 * Requiere `modelName` e `itemData` en el componente.
 */
export default {
  data() {
    return {
      /** Listados traídos por el propio componente cuando no le llegan por props. */
      operationCurrencyLists: [],
      /** Cotizaciones vigentes por moneda; null mientras no se consultaron. */
      operationCurrencyRates: null,
    };
  },
  computed: {
    /** Id de la moneda del documento: la de la factura al cliente en el embarque. */
    operationCurrencyId() {
      const item = this.itemData || {};
      const isShipping = String(this.modelName || "").toLowerCase() === "shipping";
      const raw = isShipping ? item.currency_in || item.currency_ps : item.currency_ps;
      if (raw === null || raw === undefined || raw === "") return "";
      const id = typeof raw === "object" && raw._id != null ? raw._id : raw;
      return String(id || "");
    },
    operationCurrencyRows() {
      const lists = [
        ...(Array.isArray(this.lists) ? this.lists : []),
        ...(this.operationCurrencyLists || []),
      ];
      const doc = lists.find(
        (l) => l && l.type === "currency" && Array.isArray(l.values) && l.values.length
      );
      return doc ? doc.values.filter((v) => v && v._id) : [];
    },
    operationCurrencyLabel() {
      const id = this.operationCurrencyId;
      if (!id) return "";
      const found = this.operationCurrencyRows.find((v) => String(v._id) === id);
      return String((found && (found.label || found.name)) || "")
        .trim()
        .toUpperCase();
    },
    /**
     * Prefijo que lleva cada importe del panel.
     * Sin moneda resuelta queda el "$" de siempre, para no empeorar lo que ya se veía.
     */
    moneyPrefix() {
      const label = this.operationCurrencyLabel;
      if (!label) return "$";
      return UNMISTAKABLE_SYMBOLS[label] || `${label} `;
    },
    /**
     * Signo de los márgenes: la moneda en que se calcularon. Con exchange_rates_module es la
     * del sistema (USD), aunque la operación se facture en otra.
     */
    marginMoneyPrefix() {
      const m = this.balanceData && this.balanceData.margins;
      return m && m.currencyId ? this.currencyPrefixFor(m.currencyId) : this.moneyPrefix;
    },
    /** Monedas que intervienen en la operación: la suya y la de cada gasto. */
    operationCurrenciesInvolved() {
      const ids = new Set();
      if (this.operationCurrencyId) ids.add(this.operationCurrencyId);
      const costs = (this.balanceData && this.balanceData.costs) || [];
      costs.forEach((c) => {
        if (c && c.currencyId) ids.add(String(c.currencyId));
      });
      return Array.from(ids);
    },
    /**
     * Monedas sin cotización cargada que hacen falta para esta operación.
     *
     * Con una sola moneda en juego no hay nada que convertir y el margen se calcula igual,
     * aunque esa moneda no tenga cotización: avisar ahí sería alarma falsa.
     */
    missingRateCurrencies() {
      // Con tipo de cambio de operación (exchange_rates_module) el balance ya dice qué
      // moneda se quedó sin cotización.
      // En la PI viene uno por embarque.
      const fx = this.balanceData && this.balanceData.fx;
      const fxRates = fx ? (fx.byShipping ? Object.values(fx.byShipping).flat() : fx.rates) : null;
      if (Array.isArray(fxRates)) {
        return [
          ...new Set(
            fxRates
              .filter((r) => r && r.missing)
              .map((r) => String(r.label || "").toUpperCase())
              .filter(Boolean)
          ),
        ];
      }
      const involved = this.operationCurrenciesInvolved;
      if (involved.length < 2 || !Array.isArray(this.operationCurrencyRates)) return [];
      return involved
        .map((id) =>
          this.operationCurrencyRates.find((r) => r && String(r.currencyId) === id)
        )
        .filter(
          (row) =>
            row &&
            !row.isSystemCurrency &&
            (row.rate === null || row.rate === undefined || row.rate === "")
        )
        .map((row) => String(row.currencyLabel || "").toUpperCase())
        .filter(Boolean);
    },
    /**
     * Hay que convertir entre monedas y falta alguna cotización: el margen queda vacío.
     */
    operationCurrencyRateMissing() {
      return this.missingRateCurrencies.length > 0;
    },
    /** Monedas faltantes, para el texto del aviso. */
    missingRateCurrenciesLabel() {
      return this.missingRateCurrencies.join(", ");
    },
  },
  methods: {
    /**
     * Prefijo de una moneda puntual: lo usa cada gasto, que puede estar cargado en una
     * moneda distinta a la de la operación. Sin moneda propia cae en la de la operación.
     * @param {*} currencyId
     * @returns {string}
     */
    currencyPrefixFor(currencyId) {
      if (currencyId === null || currencyId === undefined || currencyId === "") {
        return this.moneyPrefix;
      }
      const id =
        typeof currencyId === "object" && currencyId._id != null
          ? String(currencyId._id)
          : String(currencyId);
      if (id === this.operationCurrencyId) return this.moneyPrefix;
      const found = this.operationCurrencyRows.find((v) => String(v._id) === id);
      const label = String((found && (found.label || found.name)) || "")
        .trim()
        .toUpperCase();
      if (!label) return this.moneyPrefix;
      return UNMISTAKABLE_SYMBOLS[label] || `${label} `;
    },
    async ensureOperationCurrencyList() {
      if (this.operationCurrencyRows.length) return;
      try {
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.operationCurrencyLists = Array.isArray(data) ? data : [];
      } catch {
        this.operationCurrencyLists = [];
      }
    },
    async ensureOperationCurrencyRates() {
      if (Array.isArray(this.operationCurrencyRates)) return;
      try {
        const { data } = await wencesApi.get(
          "/api/gotrading/currency-rate-history/current-rates"
        );
        this.operationCurrencyRates = Array.isArray(data && data.data) ? data.data : [];
      } catch {
        this.operationCurrencyRates = [];
      }
    },
  },
  mounted() {
    this.ensureOperationCurrencyList();
    this.ensureOperationCurrencyRates();
  },
};
